/**
 * StudentInfoStep.js
 *
 * Step 1 of the clearance wizard.
 * Displays student information and clearance assignment details.
 * Allows user to proceed to item selection.
 *
 * @component
 */
import React from "react";
import {
  Descriptions,
  Tag,
  Button,
  Space,
  Alert,
  Typography,
  Divider,
} from "antd";
import {
  ArrowRightOutlined,
  UserOutlined,
  BookOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

/**
 * Returns color for status tag
 */
const getStatusColor = (status) => {
  const colors = {
    "Pending Selection": "orange",
    "Items Selected": "blue",
    "Pending Payment": "gold",
    "Partially Paid": "cyan",
    Paid: "green",
    "Ready for Pickup": "purple",
    Cleared: "success",
  };
  return colors[status] || "default";
};

/**
 * StudentInfoStep Component
 *
 * @param {object} clearance - The clearance data
 * @param {function} onNext - Callback to proceed to next step
 */
const StudentInfoStep = ({ clearance, onNext }) => {
  if (!clearance) return null;

  const requirementItemsCount = clearance.requirement_items?.length || 0;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>
          <UserOutlined style={{ marginRight: 8 }} />
          Student Information
        </Title>
        <Text type="secondary">
          Review the student details and clearance assignment before selecting
          items.
        </Text>
      </div>

      {/* Student Details */}
      <Descriptions
        bordered
        column={{ xs: 1, sm: 2, md: 2 }}
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label="Student Name" span={2}>
          <Text strong style={{ fontSize: 16 }}>
            {clearance.student_name}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="SPC Code">
          <Text code copyable>
            {clearance.student_spc}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="BOTPC">
          <Text code copyable>
            {clearance.botpc}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Class">
          {clearance.class_name || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label="Current Status">
          <Tag color={getStatusColor(clearance.status)}>{clearance.status}</Tag>
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      {/* Academic Info */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>
          <CalendarOutlined style={{ marginRight: 8 }} />
          Academic Information
        </Title>
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Term">
            {clearance.term_name || `Term ${clearance.term}`}
          </Descriptions.Item>
          <Descriptions.Item label="Academic Year">
            {clearance.acadyr}
          </Descriptions.Item>
          {clearance.payment_deadline && (
            <Descriptions.Item label="Payment Deadline">
              <Text type="warning">{clearance.payment_deadline}</Text>
            </Descriptions.Item>
          )}
          {clearance.pickup_deadline && (
            <Descriptions.Item label="Pickup Deadline">
              {clearance.pickup_deadline}
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>

      <Divider />

      {/* Template & Items Info */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>
          <BookOutlined style={{ marginRight: 8 }} />
          Requirement Template
        </Title>
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Template Name" span={2}>
            {clearance.template_name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Required Items">
            <Text strong>{requirementItemsCount} item(s)</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Items Selected">
            {clearance.selections?.length || 0} of {requirementItemsCount}
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* Instructions */}
      <Alert
        message="Next Steps"
        description={
          clearance.status === "Pending Selection"
            ? "Click 'Proceed to Item Selection' to choose brands and sizes for each required item."
            : clearance.status === "Items Selected"
              ? "You have already selected items. Click 'Proceed' to review your selections or make changes."
              : "Continue to the next step to complete your clearance."
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Financial Summary (if any selections exist) */}
      {clearance.total_amount > 0 && (
        <div
          style={{
            padding: 16,
            background: "#f6ffed",
            borderRadius: 8,
            border: "1px solid #b7eb8f",
            marginBottom: 24,
          }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>Total Amount:</Text>
              <Text strong>
                UGX {parseFloat(clearance.total_amount).toLocaleString()}
              </Text>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>Amount Paid:</Text>
              <Text type="success">
                UGX {parseFloat(clearance.amount_paid || 0).toLocaleString()}
              </Text>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>Balance:</Text>
              <Text
                strong
                type={parseFloat(clearance.balance) > 0 ? "danger" : "success"}
              >
                UGX {parseFloat(clearance.balance || 0).toLocaleString()}
              </Text>
            </div>
          </Space>
        </div>
      )}

      {/* Action Button */}
      <div style={{ textAlign: "right" }}>
        <Button
          type="primary"
          size="large"
          icon={<ArrowRightOutlined />}
          onClick={onNext}
        >
          Proceed to Item Selection
        </Button>
      </div>
    </div>
  );
};

export default StudentInfoStep;
